import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ArcGISFeature, ArcGISDrawingInfo, ArcGISLayerDefinition, ArcGISLayerMetadata, ArcGISOperationalLayer, ArcGISExtent, ServiceTokenContext, PBFCapabilities, ArcGISIdentifyParams, ArcGISIdentifyResult, ArcGISIdentifyResponse } from '../models/arcgis-types';
import { ArcGISUserService } from './arcgis-user-service';
import { ArcGISOptimizedFetcherService } from './arcgis-optimized-fetcher.service';
import { ArcGISTokenManagerService } from './arcgis-token-manager.service';
import { Extent } from 'ol/extent';
import { transformExtent } from 'ol/proj';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { ARCGIS_CONSTANTS } from '../constants/arcgis-constants';
import { ArcGISItemStyleService } from './arcgis-item-style.service';
import { resolveArcGISItemLayerDefinition } from '../utils/arcgis-item-style.util';
import { getCIMRendererError } from '../utils/arcgis-cim-style.util';
import { normalizeArcGISRenderer } from '../utils/arcgis-renderer.util';

@Injectable({
    providedIn: 'root'
})
export class ArcGISLayerCacheService {
    private featureCache = new Map<string, ArcGISFeature[]>();
    private layerMetadataCache = new Map<string, ArcGISLayerMetadata>();
    private layerDefinitionCache = new Map<string, ArcGISLayerDefinition>();
    private drawingInfoVersion = 0;
    private geometryTypeCache = new Map<string, string>();
    private extentCache = new Map<string, Extent>();
    private pbfCapabilitiesCache = new Map<string, PBFCapabilities>();
    private inFlightFeatureRequests = new Map<string, Promise<ArcGISFeature[]>>();
    private inFlightMetadataRequests = new Map<string, Promise<ArcGISLayerMetadata>>();

    private featureCacheChanged = new BehaviorSubject<Map<string, ArcGISFeature[]>>(new Map());
    private layerMetadataCacheChanged = new BehaviorSubject<Map<string, ArcGISLayerMetadata>>(new Map());

    private cacheHits = 0;
    private cacheMisses = 0;

    private currentSessionId: string | null = null;
    private currentAbortController: AbortController | null = null;

    constructor(
        private arcgisUserService: ArcGISUserService,
        private arcgisOptimizedFetcher: ArcGISOptimizedFetcherService,
        private arcgisTokenManager: ArcGISTokenManagerService,
        private arcgisItemStyle: ArcGISItemStyleService
    ) {
        this.registerProj4();
        this.setupSignOutCleanup();
    }

    private registerProj4(): void {
        register(proj4);
    }

    private setupSignOutCleanup(): void {
        this.arcgisUserService.arcGISUserTokenChanged.subscribe(token => {
            if (token === null) {
                this.endSession();
            }
        });
    }

    public startNewSession(): string {
        this.endSession();

        this.currentSessionId = this.generateSessionId();
        this.currentAbortController = new AbortController();

        return this.currentSessionId;
    }

    public endSession(): void {
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }
        this.currentSessionId = null;
        this.clearAllCaches();
    }

    public getSessionId(): string | null {
        return this.currentSessionId;
    }

    public getAbortSignal(): AbortSignal | undefined {
        return this.currentAbortController?.signal;
    }

    public isSessionValid(sessionId: string | null): boolean {
        return sessionId !== null && this.currentSessionId === sessionId;
    }

    private generateSessionId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substring(2);
    }

    private getCacheKey(serviceUrl: string, layerId: number | string): string {
        return serviceUrl + '/' + layerId.toString();
    }

    public async getFeatures(serviceUrl: string, layerId: number | string, context?: ServiceTokenContext): Promise<ArcGISFeature[]> {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);
        const signal = this.getAbortSignal();
        const sessionId = this.currentSessionId;

        if (this.featureCache.has(cacheKey)) {
            this.cacheHits++;
            return this.featureCache.get(cacheKey)!;
        }

        if (this.inFlightFeatureRequests.has(cacheKey)) {
            return this.inFlightFeatureRequests.get(cacheKey)!;
        }

        this.cacheMisses++;

        const requestPromise = this.fetchFeaturesWithOptimization(serviceUrl, layerId, context, cacheKey, signal, sessionId);
        this.inFlightFeatureRequests.set(cacheKey, requestPromise);

        return requestPromise;
    }

    private async fetchFeaturesWithOptimization(
        serviceUrl: string,
        layerId: number | string,
        context: ServiceTokenContext | undefined,
        cacheKey: string,
        signal: AbortSignal | undefined,
        sessionId: string | null
    ): Promise<ArcGISFeature[]> {
        try {
            if (signal?.aborted) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            const [pbfCapabilities, layerExtent] = await Promise.all([
                this.getPBFCapabilities(serviceUrl, layerId, context, signal),
                this.getLayerFullExtent(serviceUrl, layerId, context, signal)
            ]);

            if (signal?.aborted) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            const features = await this.arcgisOptimizedFetcher.fetchAllFeatures(
                serviceUrl,
                layerId,
                context,
                pbfCapabilities,
                layerExtent,
                signal
            );

            if (!this.isSessionValid(sessionId)) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_SESSION_INVALIDATED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            this.featureCache.set(cacheKey, features);
            this.featureCacheChanged.next(this.featureCache);
            return features;
        } catch (error) {
            if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                console.debug('Feature fetch aborted for ' + cacheKey);
            } else {
                console.error('Failed to fetch features for ' + cacheKey + ':', error);
            }
            throw error;
        } finally {
            this.inFlightFeatureRequests.delete(cacheKey);
        }
    }

    public async getLayerMetadata(serviceUrl: string, layerId: number | string, context?: ServiceTokenContext, signal?: AbortSignal): Promise<ArcGISLayerMetadata> {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);
        const effectiveSignal = signal || this.getAbortSignal();

        if (this.layerMetadataCache.has(cacheKey)) {
            return this.layerMetadataCache.get(cacheKey)!;
        }

        if (this.inFlightMetadataRequests.has(cacheKey)) {
            return this.inFlightMetadataRequests.get(cacheKey)!;
        }

        const drawingInfoVersion = this.drawingInfoVersion;
        const requestPromise = this.fetchLayerMetadata(serviceUrl, layerId, context, effectiveSignal)
            .then(metadata => {
                if (drawingInfoVersion === this.drawingInfoVersion) {
                    this.layerMetadataCache.set(cacheKey, metadata);
                    this.layerMetadataCacheChanged.next(this.layerMetadataCache);
                }
                return metadata;
            })
            .catch(error => {
                if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                    console.debug('Metadata fetch aborted for ' + cacheKey);
                } else {
                    console.error('Failed to fetch metadata for ' + cacheKey + ':', error);
                }
                throw error;
            })
            .finally(() => {
                if (this.inFlightMetadataRequests.get(cacheKey) === requestPromise) {
                    this.inFlightMetadataRequests.delete(cacheKey);
                }
            });

        this.inFlightMetadataRequests.set(cacheKey, requestPromise);

        return requestPromise;
    }

    public async getDrawingInfo(serviceUrl: string, layerId: number | string, webMapId?: string, context?: ServiceTokenContext): Promise<ArcGISDrawingInfo | null> {
        const definition = await this.getLayerDefinition(serviceUrl, layerId, webMapId, context);
        return definition?.drawingInfo ?? null;
    }

    public async getLayerDefinition(serviceUrl: string, layerId: number | string, webMapId?: string, context?: ServiceTokenContext): Promise<ArcGISLayerDefinition | null> {
        const cacheKey = webMapId
            ? 'webmap_' + webMapId + '_' + layerId.toString()
            : this.getCacheKey(serviceUrl, layerId) + (context?.itemId ? '_item_' + context.itemId : '');
        const signal = this.getAbortSignal();
        const sessionId = this.currentSessionId;
        const drawingInfoVersion = this.drawingInfoVersion;

        const cached = this.layerDefinitionCache.get(cacheKey);
        if (cached) return cached;

        try {
            if (signal?.aborted) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            const definition = await this.fetchLayerDefinition(serviceUrl, layerId, webMapId, context, signal);
            let drawingInfo = definition.drawingInfo;
            if (drawingInfo?.renderer) {
                const renderer = normalizeArcGISRenderer(drawingInfo.renderer);
                if (renderer !== drawingInfo.renderer) drawingInfo = { ...drawingInfo, renderer };
                const unsupported = getCIMRendererError(drawingInfo.renderer);
                if (unsupported) {
                    const metadata = await this.getLayerMetadata(serviceUrl, layerId, context, signal);
                    drawingInfo = metadata.drawingInfo && !getCIMRendererError(metadata.drawingInfo.renderer) ? metadata.drawingInfo : undefined;
                }
            }

            if (!this.isSessionValid(sessionId) || drawingInfoVersion !== this.drawingInfoVersion) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_SESSION_INVALIDATED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            const resolved: ArcGISLayerDefinition = { ...definition, drawingInfo };
            this.layerDefinitionCache.set(cacheKey, resolved);
            return resolved;
        } catch (error) {
            if ((error instanceof Error || error instanceof DOMException) && error.name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                console.debug('Drawing info fetch aborted for ' + cacheKey);
                return null;
            }
            console.error('Failed to fetch drawing info for ' + cacheKey + ':', error);
            return null;
        }
    }

    public async getLayerForPlot(
        serviceUrl: string,
        layerId: number | string,
        webMapId?: string,
        context?: ServiceTokenContext,
        drawingInfoVersion = this.drawingInfoVersion,
        shouldPlot: () => boolean = () => true,
    ): Promise<{ drawingInfo: ArcGISDrawingInfo | null; features: ArcGISFeature[] } | null> {
        if (!shouldPlot()) return null;
        const sessionId = this.currentSessionId;
        const drawingInfo = await this.getDrawingInfo(serviceUrl, layerId, webMapId, context);
        if (!shouldPlot() || !this.isSessionValid(sessionId) || drawingInfoVersion !== this.drawingInfoVersion) {
            return null;
        }
        const features = await this.getFeatures(serviceUrl, layerId, context);
        return shouldPlot() && this.isSessionValid(sessionId) && drawingInfoVersion === this.drawingInfoVersion
            ? { drawingInfo, features }
            : null;
    }

    public async getPBFCapabilities(serviceUrl: string, layerId: number | string, context?: ServiceTokenContext, signal?: AbortSignal): Promise<PBFCapabilities> {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);
        const effectiveSignal = signal || this.getAbortSignal();

        if (this.pbfCapabilitiesCache.has(cacheKey)) {
            return this.pbfCapabilitiesCache.get(cacheKey)!;
        }

        try {
            const metadata = await this.getLayerMetadata(serviceUrl, layerId, context, effectiveSignal);
            const capabilities = this.extractPBFCapabilities(metadata);
            this.pbfCapabilitiesCache.set(cacheKey, capabilities);
            return capabilities;
        } catch (error) {
            const defaultCapabilities: PBFCapabilities = {
                supportsPBF: false,
                supportsQuantization: false,
                maxRecordCount: 2000
            };
            return defaultCapabilities;
        }
    }

    private extractPBFCapabilities(metadata: ArcGISLayerMetadata | null): PBFCapabilities {
        if (!metadata) {
            return {
                supportsPBF: false,
                supportsQuantization: false,
                maxRecordCount: 2000
            };
        }

        const supportedFormats = metadata.supportedQueryFormats || '';
        const supportsPBF = supportedFormats.toUpperCase().includes('PBF');
        const supportsQuantization = metadata.advancedQueryCapabilities?.supportsCoordinatesQuantization === true;

        return {
            supportsPBF: supportsPBF && supportsQuantization,
            supportsQuantization: supportsQuantization,
            maxRecordCount: metadata.maxRecordCount || 2000,
            tileMaxRecordCount: metadata.tileMaxRecordCount,
            standardMaxRecordCount: metadata.standardMaxRecordCount
        };
    }

    public async getLayerFullExtent(serviceUrl: string, layerId: number | string, context?: ServiceTokenContext, signal?: AbortSignal): Promise<ArcGISExtent | null> {
        const effectiveSignal = signal || this.getAbortSignal();
        try {
            const metadata = await this.getLayerMetadata(serviceUrl, layerId, context, effectiveSignal);
            return metadata?.extent || null;
        } catch (error) {
            return null;
        }
    }

    public getGeometryType(serviceUrl: string, layerId: number | string): string | null {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);
        return this.geometryTypeCache.get(cacheKey) || null;
    }

    public setGeometryType(serviceUrl: string, layerId: number | string, geometryType: string): void {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);
        this.geometryTypeCache.set(cacheKey, geometryType);
    }

    public hasCachedFeatures(serviceUrl: string, layerId: number | string): boolean {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);
        return this.featureCache.has(cacheKey);
    }

    public getDrawingInfoVersion(): number {
        return this.drawingInfoVersion;
    }

    public clearDrawingInfoCache(): void {
        this.drawingInfoVersion++;
        this.layerDefinitionCache.clear();
        this.layerMetadataCache.clear();
        this.inFlightMetadataRequests.clear();
        this.arcgisItemStyle.clearRequests();
    }

    public clearAllCaches(): void {
        this.featureCache.clear();
        this.layerMetadataCache.clear();
        this.clearDrawingInfoCache();
        this.geometryTypeCache.clear();
        this.extentCache.clear();
        this.pbfCapabilitiesCache.clear();
        this.inFlightFeatureRequests.clear();
        this.inFlightMetadataRequests.clear();

        this.featureCacheChanged.next(new Map());
        this.layerMetadataCacheChanged.next(new Map());

        this.cacheHits = 0;
        this.cacheMisses = 0;

        this.arcgisOptimizedFetcher.clearPBFFailureCache();
    }

    public getTokenForService(): string | null {
        return this.arcgisUserService.getToken();
    }

    public buildUrlWithCorrectToken(url: string, context?: ServiceTokenContext): string {
        return this.arcgisTokenManager.buildUrlWithToken(url, context);
    }

    public getCacheStats(): { features: number; metadata: number; drawingInfo: number; geometryTypes: number; hits: number; misses: number; hitRate: string } {
        const total = this.cacheHits + this.cacheMisses;
        const hitRate = total > 0 ? ((this.cacheHits / total) * 100).toFixed(2) : '0.00';

        return {
            features: this.featureCache.size,
            metadata: this.layerMetadataCache.size,
            drawingInfo: this.layerDefinitionCache.size,
            geometryTypes: this.geometryTypeCache.size,
            hits: this.cacheHits,
            misses: this.cacheMisses,
            hitRate: hitRate + '%'
        };
    }

    public invalidateLayer(serviceUrl: string, layerId: number | string): void {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);

        this.featureCache.delete(cacheKey);
        this.layerMetadataCache.delete(cacheKey);
        this.clearDrawingInfoCache();
        this.geometryTypeCache.delete(cacheKey);
        this.extentCache.delete(cacheKey);
        this.pbfCapabilitiesCache.delete(cacheKey);
    }

    private async fetchLayerMetadata(serviceUrl: string, layerId: number | string, context?: ServiceTokenContext, signal?: AbortSignal): Promise<ArcGISLayerMetadata> {
        const layerUrl = serviceUrl + '/' + layerId.toString();
        const response = await this.arcgisTokenManager.fetchWithAuthRetry(
            force => this.arcgisTokenManager.buildUrlWithToken(layerUrl, context, force),
            { signal }
        );

        if (!response.ok) {
            throw new Error('Failed to fetch layer metadata: ' + response.statusText);
        }

        return await response.json();
    }

    private async fetchLayerDefinition(serviceUrl: string, layerId: number | string, webMapId?: string, context?: ServiceTokenContext, signal?: AbortSignal): Promise<ArcGISLayerDefinition> {
        let webMapLayer: ArcGISOperationalLayer | undefined;
        if (webMapId) {
            if (signal?.aborted) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }
            const webMapData = await this.arcgisUserService.fetchWebMapData(webMapId);
            webMapLayer = webMapData?.operationalLayers?.find(layer => layer.id === layerId.toString());
        }

        const metadata = await this.getLayerMetadata(serviceUrl, layerId, context, signal);
        let definition: ArcGISLayerDefinition = {
            drawingInfo: metadata?.drawingInfo,
            minScale: metadata?.minScale,
            maxScale: metadata?.maxScale
        };
        if (/\/FeatureServer\/?(?:\?|$)/i.test(serviceUrl)) {
            const itemData = await this.arcgisItemStyle.getItemData(
                this.arcgisUserService.getPortalUrl(),
                context?.itemId || webMapLayer?.itemId || metadata?.serviceItemId,
                this.arcgisUserService.getToken()
            );
            definition = resolveArcGISItemLayerDefinition(definition, itemData, layerId);
        }
        return {
            drawingInfo: webMapLayer?.layerDefinition?.drawingInfo ?? definition.drawingInfo,
            minScale: webMapLayer?.layerDefinition?.minScale ?? webMapLayer?.minScale ?? definition.minScale,
            maxScale: webMapLayer?.layerDefinition?.maxScale ?? webMapLayer?.maxScale ?? definition.maxScale
        };
    }

    public async getLayerExtent(serviceUrl: string, layerId: number | string, context?: ServiceTokenContext): Promise<Extent | null> {
        const cacheKey = this.getCacheKey(serviceUrl, layerId);
        const signal = this.getAbortSignal();

        if (this.extentCache.has(cacheKey)) {
            return this.extentCache.get(cacheKey)!;
        }

        try {
            const extent = await this.fetchLayerExtent(serviceUrl, layerId, context, signal);
            if (extent) {
                this.extentCache.set(cacheKey, extent);
            }
            return extent;
        } catch (error) {
            if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                return null;
            }
            return await this.fetchServiceExtent(serviceUrl, context, signal);
        }
    }

    private async fetchLayerExtent(serviceUrl: string, layerId: number | string, context?: ServiceTokenContext, signal?: AbortSignal): Promise<Extent | null> {
        const layerUrl = serviceUrl + '/' + layerId.toString();
        const response = await this.arcgisTokenManager.fetchWithAuthRetry(
            force => this.arcgisTokenManager.buildUrlWithToken(layerUrl, context, force),
            { signal }
        );

        if (!response.ok) {
            throw new Error('HTTP ' + response.status.toString() + ': ' + response.statusText);
        }

        const metadata = await response.json();

        if (!metadata.extent) {
            return null;
        }

        return await this.convertArcGISExtent(metadata.extent);
    }

    private async fetchServiceExtent(serviceUrl: string, context?: ServiceTokenContext, signal?: AbortSignal): Promise<Extent | null> {
        try {
            const response = await this.arcgisTokenManager.fetchWithAuthRetry(
                force => this.arcgisTokenManager.buildUrlWithToken(serviceUrl, context, force),
                { signal }
            );

            if (!response.ok) {
                return null;
            }

            const metadata = await response.json();
            const arcgisExtent: ArcGISExtent = metadata.fullExtent || metadata.initialExtent;

            if (!arcgisExtent) {
                return null;
            }

            return await this.convertArcGISExtent(arcgisExtent);
        } catch (error) {
            return null;
        }
    }

    private async convertArcGISExtent(arcgisExtent: ArcGISExtent): Promise<Extent | null> {
        const extent: Extent = [
            arcgisExtent.xmin,
            arcgisExtent.ymin,
            arcgisExtent.xmax,
            arcgisExtent.ymax
        ];

        const sourceWkid = arcgisExtent.spatialReference?.wkid || arcgisExtent.spatialReference?.latestWkid;
        const webMercatorWkids = [3857, 102100, 102113, 3785, 900913];

        if (!sourceWkid || webMercatorWkids.includes(sourceWkid)) {
            return extent;
        }

        try {
            const transformedExtent = transformExtent(extent, 'EPSG:' + sourceWkid.toString(), 'EPSG:3857');
            return transformedExtent;
        } catch (error) {
            const registered = await this.registerProjectionDynamically(sourceWkid);
            if (registered) {
                try {
                    const transformedExtent = transformExtent(extent, 'EPSG:' + sourceWkid.toString(), 'EPSG:3857');
                    return transformedExtent;
                } catch (retryError) {
                    return extent;
                }
            }

            return extent;
        }
    }

    private async registerProjectionDynamically(wkid: number): Promise<boolean> {
        const signal = this.getAbortSignal();
        try {
            const response = await fetch('https://epsg.io/' + wkid.toString() + '.proj4', { signal });
            if (!response.ok) {
                return false;
            }

            const proj4def = await response.text();
            proj4.defs('EPSG:' + wkid.toString(), proj4def);
            register(proj4);

            return true;
        } catch (error) {
            return false;
        }
    }

    public async identify(
        serviceUrl: string,
        params: ArcGISIdentifyParams,
        context?: ServiceTokenContext
    ): Promise<ArcGISIdentifyResult[]> {
        const identifyUrl = serviceUrl + '/identify';
        const body = new URLSearchParams({
            geometry: params.geometry,
            geometryType: params.geometryType,
            sr: params.sr,
            layers: params.layers,
            tolerance: params.tolerance,
            mapExtent: params.mapExtent,
            imageDisplay: params.imageDisplay,
            returnGeometry: params.returnGeometry,
            f: params.f
        });

        const token = this.arcgisTokenManager.getTokenForService(serviceUrl, context);
        if (token) {
            body.set(ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN, token);
        }

        const signal = this.getAbortSignal();
        const response = await fetch(identifyUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
            signal
        });
        if (!response.ok) {
            throw new Error('Failed to identify features: ' + response.statusText);
        }

        const data: ArcGISIdentifyResponse = await response.json();
        return data.results || [];
    }
}
